import { useEffect, useId, useRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/ui/button'
import { DialogFooter } from '@/ui/dialog'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { RolePicker } from '@/ui/role-picker'
import { apiErrorMessage } from '@/lib/api-error-message'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import { useAssignableRoles } from '@/modules/iam/features/roles/role-queries'

import {
  assignGroupRoleErrorMessage,
  useAssignGroupRole,
  useEntityGroups,
} from './group-queries'

const assignGroupSchema = z.object({
  groupId: z.string().min(1, 'Select a group.'),
  roleId: z.string().min(1, 'Select a role.'),
})

type AssignGroupValues = z.infer<typeof assignGroupSchema>

export function AssignGroupPanel({
  entityId,
  onClose,
  onPendingChange,
  onAssigned,
}: {
  entityId: string
  onClose: () => void
  onPendingChange?: (pending: boolean) => void
  onAssigned?: (message: string) => void
}) {
  const groupErrorId = useId()
  const roleErrorId = useId()
  const requestErrorId = useId()
  const groupsQuery = useEntityGroups(entityId)
  const assign = useAssignGroupRole(entityId)
  const rolesQuery = useAssignableRoles(entityId)
  const { control, handleSubmit, reset, setValue } = useForm<AssignGroupValues>({
    resolver: zodResolver(assignGroupSchema),
    defaultValues: { groupId: '', roleId: '' },
    mode: 'onSubmit',
  })
  const groupId = useWatch({ control, name: 'groupId' })
  const previousEntityId = useRef(entityId)
  const resetAssign = assign.reset

  useEffect(() => {
    onPendingChange?.(assign.isPending)
  }, [assign.isPending, onPendingChange])

  useEffect(() => {
    if (previousEntityId.current === entityId) return
    previousEntityId.current = entityId
    reset({ groupId: '', roleId: '' })
    resetAssign()
  }, [entityId, reset, resetAssign])

  const groups = (groupsQuery.data?.items ?? []).filter((group) => group.active)
  const selectedGroup = groups.find((g) => g.groupId === groupId) ?? null
  const heldIds = new Set(selectedGroup?.roles.map((r) => r.roleId))
  const assignableRoles = (rolesQuery.data ?? []).filter(
    (r) => !heldIds.has(r.roleId),
  )

  function confirmAssign({ groupId: nextGroupId, roleId }: AssignGroupValues) {
    const roleName = assignableRoles.find((r) => r.roleId === roleId)?.name
    const subjectName = selectedGroup?.name
    assign.mutate(
      { groupId: nextGroupId, roleId },
      {
        onSuccess: () => {
          onPendingChange?.(false)
          onAssigned?.(
            roleName && subjectName
              ? `Assigned ${roleName} to ${subjectName}`
              : 'Role assigned',
          )
          onClose()
        },
      },
    )
  }

  if (groupsQuery.isPending) return <LoadingNotice message="Loading groups…" />
  if (groupsQuery.isError && !groupsQuery.data)
    return (
      <RetryNotice
        message={apiErrorMessage(groupsQuery.error, 'Couldn’t load groups. Try again.')}
        onRetry={() => void groupsQuery.refetch()}
      />
    )

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        assign.reset()
        void handleSubmit(confirmAssign)(e)
      }}
    >
      {groups.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No groups are available to assign at this entity.
        </p>
      ) : (
        <Controller
          control={control}
          name="groupId"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1">
              <Select
                value={field.value || null}
                onValueChange={(next) => {
                  if (typeof next === 'string') {
                    field.onChange(next)
                    setValue('roleId', '')
                  }
                }}
                disabled={assign.isPending}
                items={Object.fromEntries(
                  groups.map((g) => [g.groupId, g.name]),
                )}
              >
                <SelectTrigger
                  aria-label="Group"
                  aria-invalid={fieldState.invalid}
                  aria-describedby={
                    fieldState.error ? groupErrorId : undefined
                  }
                  onBlur={field.onBlur}
                  ref={field.ref}
                >
                  <SelectValue placeholder="Select a group…" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g.groupId} value={g.groupId}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldState.error ? (
                <p
                  id={groupErrorId}
                  role="alert"
                  className="text-destructive text-xs"
                >
                  {fieldState.error.message}
                </p>
              ) : null}
            </div>
          )}
        />
      )}

      <Controller
        control={control}
        name="roleId"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-1">
            <RolePicker
              aria-label="Role"
              roles={assignableRoles}
              isLoading={rolesQuery.isLoading}
              error={rolesQuery.error}
              onRetry={() => void rolesQuery.refetch()}
              value={field.value || null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              aria-invalid={fieldState.invalid || assign.isError}
              aria-describedby={
                fieldState.error
                  ? roleErrorId
                  : assign.isError
                    ? requestErrorId
                    : undefined
              }
              disabled={assign.isPending || !groupId}
            />
            {fieldState.error ? (
              <p
                id={roleErrorId}
                role="alert"
                className="text-destructive text-xs"
              >
                {fieldState.error.message}
              </p>
            ) : null}
          </div>
        )}
      />

      {assign.isError ? (
        <p id={requestErrorId} role="alert" className="text-destructive">
          {assignGroupRoleErrorMessage(assign.error)}
        </p>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={assign.isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={
            assign.isPending ||
            groups.length === 0 ||
            rolesQuery.isLoading ||
            assignableRoles.length === 0
          }
        >
          {assign.isPending ? 'Assigning…' : 'Assign role'}
        </Button>
      </DialogFooter>
    </form>
  )
}
